import { useCallback, useEffect, useState } from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { trpcUtils } from "@editor/utils/trpc";

import { errorToast } from "@replo/design-system/components/alert/Toast";
import { showEntri } from "entrijs";

import useSetupValidatedDomain from "./useSetupValidatedDomain";

interface EntriCallbackData {
  domain: string | null;
  success: boolean;
  setupType:
    | "automatic"
    | "manual"
    | "sharedLogin"
    | "semiautomatic"
    | "purchase"
    | null;
  provider: string;
  lastStatus: string;
  error?: {
    code: string;
    title: string;
    details: string;
  };
}

interface EntriCloseEvent extends CustomEvent {
  detail: EntriCallbackData;
}

type DNSRecordType =
  | "A"
  | "AAAA"
  | "CNAME"
  | "MX"
  | "TXT"
  | "NS"
  | "PTR"
  | "SRV"
  | "CAA";

interface DNSRecord {
  host: string;
  ttl: number;
  type: DNSRecordType;
  value: string;
  fallbackValue?: string;
  priority?: number; // Only for MX records
}

interface EntriConfig {
  applicationId: string;
  dnsRecords: DNSRecord[];
  token: string;
  applicationName?: string;
  defaultSubdomain?: string;
  forceSubdomain?: boolean;
  hostRequired?: boolean;
  manualSetupDocumentation?: string;
  supportForSubdomains?: boolean;
  userId?: string;
  wwwRedirect?: boolean;
  prefilledDomain?: string;
}

const constructDNSRecords = (shopDomainPrefix: string): DNSRecord[] => {
  return [
    {
      host: "{SUBDOMAIN}",
      type: "CNAME",
      value: `${shopDomainPrefix}.reploshops.com`,
      ttl: 300,
    },
  ];
};

/**
 * Hook that combines Entri modal event listening and provides a callback to show the modal.
 * Returns a function that can be called to trigger the Entri modal with proper configuration.
 */
const useEntriModal = ({
  applicationId,
  token,
  onSuccess,
  onError,
}: {
  applicationId: string;
  token: string;
  onSuccess: () => void;
  onError: () => void;
}) => {
  const [isEntriModalInProgress, setIsEntriModalInProgress] = useState(false);

  const { projectId } = useCurrentProjectContext();

  const { setupValidatedDomain } = useSetupValidatedDomain();

  const handleOnEntriCloseAsync = useCallback(
    async (event: EntriCloseEvent) => {
      const { domain, success, setupType, provider, lastStatus, error } =
        event.detail;

      if (success && domain && projectId) {
        console.info("Domain setup successful:", {
          domain,
          setupType,
          provider,
          lastStatus,
        });

        const { ok } =
          await trpcUtils.v2CustomDomain.checkCustomDomainForReploshopsCname.fetch(
            {
              projectId,
              customDomain: domain,
            },
          );

        if (!ok) {
          errorToast(
            "Domain Setup Failed",
            "Unable to verify that the domain records are set up correctly. Please try again.",
          );

          void trpcUtils.v2CustomDomain.getByProjectId.invalidate({
            projectId,
          });
          onError();
          setIsEntriModalInProgress(false);
          return;
        }

        await setupValidatedDomain(domain);
        onSuccess();
      } else {
        console.error("Domain setup failed:", {
          domain,
          provider,
          lastStatus,
          error,
        });
      }

      setIsEntriModalInProgress(false);
    },
    [projectId, setupValidatedDomain, onSuccess, onError],
  );

  const handleOnEntriClose = useCallback(
    (event: EntriCloseEvent) => {
      // Handle async logic without blocking the event handler
      void handleOnEntriCloseAsync(event);
    },
    [handleOnEntriCloseAsync],
  );

  useEffect(() => {
    window.addEventListener(
      "onEntriClose",
      handleOnEntriClose as EventListener,
    );

    return () => {
      window.removeEventListener(
        "onEntriClose",
        handleOnEntriClose as EventListener,
      );
    };
  }, [handleOnEntriClose]);

  const showEntriModal = useCallback(
    (shopDomainPrefix: string, customDomain: string) => {
      setIsEntriModalInProgress(true);

      const isRootDomain = customDomain.split(".").length === 2;
      if (isRootDomain) {
        customDomain = `www.${customDomain}`;
      }

      const dnsRecords = constructDNSRecords(shopDomainPrefix);

      const config: EntriConfig = {
        applicationId,
        dnsRecords,
        token,
        applicationName: "Replo",
        prefilledDomain: customDomain,
        wwwRedirect: isRootDomain,
      };

      void showEntri(config);
    },
    [applicationId, token],
  );

  return { showEntriModal, isEntriModalInProgress };
};

export default useEntriModal;
